"use client";

import { DrawerPreview as Drawer } from "@base-ui/react/drawer";
import { Popover } from "@base-ui/react/popover";
import type React from "react";

import { useIsMobile } from "../../../_hooks/use-responsive-value";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** PC Popover のアンカー要素。Popover.Positioner の `anchor` に渡す。 */
  anchorEl?: HTMLElement | null;
  ariaLabelledBy?: string;
  /**
   * PC Popover の背景を暗転させるかどうか。
   * true (default): Popover.Backdrop (`bg-black/40`) を描画する。
   * false: Backdrop を描画しない（Popover の outside-click dismiss は Popover 既定の挙動が担うため機能は維持される）。
   * モバイル Drawer の Backdrop は常に描画される。
   */
  dimBackdrop?: boolean;
  /**
   * 用途別レイアウト variant。幅・配置・モバイル挙動をこれ1つで決める。
   * - "calendar"（既定）: anchor 上方向・w-328・モバイルは Drawer（予約カレンダー）。
   * - "filter-sidebar": anchor 下・左寄せ・w-400・モバイルは Drawer（PC サイドバーのフィルター）。
   * - "filter-search": anchor 下・中央寄せ・モバイルでも Popover（SP 検索バーのフィルター）。
   *   幅はモバイルが画面幅full、PC/tabletはw-375。
   */
  variant?: "calendar" | "filter-sidebar" | "filter-search";
  /** ピッカーコンテンツ。h-full で高さを満たす必要がある。 */
  children: React.ReactNode;
};

/**
 * レスポンシブなピッカー用シェル (controlled)。
 * - モバイル (< 768px): Base-UI Drawer bottom sheet
 * - PC (>= 768px): Base-UI Popover (anchorEl にアンカー)
 *
 * ピッカーコンテンツ (children) はレイアウト上の位置決め・クロムのみを担い、
 * 日付ロジック等はすべて children 側に持つ。
 *
 * NOTE(重複): reservation-form の checkout-date-picker-drawer.tsx 内の
 * CheckoutDateDrawer / CheckoutDatePopover はこのシェルとほぼ同一構造（Popup 高さが 600px と
 * ~608px で差がある程度）。デザイン確定時にこのシェルへ寄せて共通化を検討する。
 */
export function ResponsivePickerShell({
  open,
  onOpenChange,
  anchorEl,
  ariaLabelledBy,
  dimBackdrop = true,
  variant = "calendar",
  children,
}: Props) {
  const isMobile = useIsMobile();
  // filter-search のみ、モバイルでも Drawer ではなく Popover（bottom-start）を使う。
  const useMobilePopover = isMobile && variant === "filter-search";
  return isMobile && !useMobilePopover ? (
    <MobileDrawerShell
      open={open}
      onOpenChange={onOpenChange}
      ariaLabelledBy={ariaLabelledBy}
    >
      {children}
    </MobileDrawerShell>
  ) : (
    <PcPopoverShell
      open={open}
      onOpenChange={onOpenChange}
      anchorEl={anchorEl}
      ariaLabelledBy={ariaLabelledBy}
      dimBackdrop={dimBackdrop}
      variant={variant}
      compact={useMobilePopover}
    >
      {children}
    </PcPopoverShell>
  );
}

/** モバイル: Base-UI Drawer (bottom sheet) */
function MobileDrawerShell({
  open,
  onOpenChange,
  ariaLabelledBy,
  children,
}: Omit<Props, "anchorEl">) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        {/* forceRender: ネストされた Drawer は既定で Backdrop を描画しないが、
            outside タップで「最前面の Drawer だけ閉じる」判定はクリック対象が
            自分の Backdrop であることを要求するため、明示的に描画する */}
        <Drawer.Backdrop
          forceRender
          className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0"
        />
        <Drawer.Viewport className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-[640px]">
          <Drawer.Popup
            aria-labelledby={ariaLabelledBy}
            // max-h ではなく h で固定する。カレンダー body は中身の高さに寄与しない
            // (absolute) ため、Popup の高さが不定だと flex-1 に配る余剰が 0 になり body が潰れる。
            className="bg-dx-surface-base flex h-[calc(100dvh-60px)] w-full flex-col overflow-hidden rounded-t-[16px] pb-[env(safe-area-inset-bottom)] shadow-lg transition-transform duration-200 data-ending-style:translate-y-full data-starting-style:translate-y-full md:h-[calc(100dvh-80px)]"
            style={{
              transform:
                "translateY(calc(var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px)))",
            }}
          >
            {/* grip bar */}
            <div className="flex shrink-0 justify-center pt-[12px] pb-[8px]">
              <div className="bg-dx-border-default h-[4px] w-[40px] rounded-full" />
            </div>
            {/* children はこの領域で高さを満たす */}
            <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/**
 * PC: Base-UI Popover。variant に応じて幅・配置を切り替える。
 * - "calendar": anchor 上方向・w-328（予約カレンダー等、従来動作）。
 * - "filter-sidebar": anchor 下・左寄せ・w-400（PC サイドバーのフィルター）。
 * - "filter-search": anchor 下・中央寄せ（SP 検索バーのフィルター）。
 *   compact=true（モバイル）は画面幅full、PC/tabletはw-375。
 */
function PcPopoverShell({
  open,
  onOpenChange,
  anchorEl,
  ariaLabelledBy,
  dimBackdrop,
  variant = "calendar",
  compact = false,
  children,
}: Omit<Props, "children"> & {
  children: React.ReactNode;
  compact?: boolean;
}) {
  // anchor が未確定（CTA 読み込み中等で null）の間は開かない。
  // → Floating UI がアンカー無しで mount→原点(0,0)に出てから再配置する「左上ジャンプ」を防ぐ。
  // フィルター/SP は anchor 常在なので実質 open のまま。
  const effectiveOpen = anchorEl ? open : false;

  const widthClass = compact
    ? "w-screen"
    : variant === "calendar"
      ? "w-[328px]"
      : variant === "filter-search"
        ? "w-[375px]"
        : "w-[400px]";

  const positionerProps =
    variant === "calendar"
      ? {
          side: "top" as const,
          // side="top" は anchor 上端基準なので、ボタン高さ分の負オフセットで重ねて下端を揃える。
          sideOffset: ({
            anchor,
          }: {
            anchor: { width: number; height: number };
          }) => -anchor.height,
          align: "center" as const,
        }
      : {
          side: "bottom" as const,
          align:
            variant === "filter-search"
              ? ("center" as const)
              : ("start" as const),
          sideOffset: 4,
          // Base UI の collisionPadding は既定 5px。align="center" かつ w-screen 幅の
          // compact popover では shift 制約が過拘束になり左右非対称に隙間ができるため 0 にする。
          ...(compact ? { collisionPadding: 0 } : {}),
        };

  return (
    <Popover.Root open={effectiveOpen} onOpenChange={onOpenChange}>
      <Popover.Portal>
        {/* ビューポート全体を暗転させる backdrop。外側クリックでの close は
            Popover 既定の dismiss 挙動が担う。dimBackdrop=false の場合は描画しない */}
        {dimBackdrop && (
          <Popover.Backdrop className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0" />
        )}
        <Popover.Positioner
          anchor={anchorEl ?? undefined}
          // anchor は fixed な Drawer 内にある可能性があるため positionMethod=fixed にする
          positionMethod="fixed"
          {...positionerProps}
          className="z-50"
        >
          <Popover.Popup
            aria-labelledby={ariaLabelledBy}
            // カレンダー body は absolute 配置で中身の高さに寄与しないため、Popup に
            // 高さを与えないと flex-1 が 0 になり潰れる。固定高さで指定する。
            // compact（SP 検索バー）はアンカーがヘッダー下にあり、viewport 相対の max-h だと
            // 下端がはみ出てフッタ（決定）が切れる。アンカー下の可用高さで clamp する。
            className={`bg-dx-surface-base flex h-[600px] flex-col overflow-hidden rounded-[16px] shadow-[0px_8px_16px_0px_rgba(0,0,0,0.1)] ${compact ? "max-h-(--available-height)" : "max-h-[calc(100dvh-120px)]"} ${widthClass}`}
          >
            {children}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
